import { ChangeEvent } from 'react';
import { Option, SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { Input } from 'reactstrap';
import { useIndustrySector } from '../industry-sector-selector/useIndustrySector';
import { DataScopeAccess } from '@g17eco/types/surveyDataScope';

export interface PackAssigmentFilters {
  sector?: string;
  industry?: string | null;
  dataShareStatus?: string;
}

interface Props {
  searchText: string;
  filters: PackAssigmentFilters;
  changeSearchText: (searchText: string) => void;
  changeFilters: (filters: PackAssigmentFilters) => void;
}

const dataShareStatusOptions = [
  {
    label: (
      <div className='d-flex align-items-center'>
        <i className='text-ThemeIconDark mr-2 fa-solid fa-circle fa-lg' />
        Full access
      </div>
    ),
    value: DataScopeAccess.Full,
  },
  {
    label: (
      <div className='d-flex align-items-center'>
        <i className='text-ThemeIconDark mr-2 fa-solid fa-circle-half-stroke fa-lg' />
        Partial access
      </div>
    ),
    value: DataScopeAccess.Partial,
  },
  {
    label: (
      <div className='d-flex align-items-center'>
        <i className='text-ThemeIconDark mr-2 fa-regular fa-circle fa-lg' />
        No access
      </div>
    ),
    value: DataScopeAccess.None,
  },
];

export const PackAssignmentFilters = (props: Props) => {
  const { searchText, filters, changeSearchText, changeFilters } = props;
  const { sectorOptions, industryOptions } = useIndustrySector(filters.sector);

  const handleChangeSearchText = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    changeSearchText(value);
  };

  const handleChangeFilters = (name: string, option: Option | null) => {
    const newFilters = { ...filters, [name]: option?.value };
    changeFilters(newFilters);
  };

  const handleChangeSectorIndustryFilters = (name: string, option: Option | null) => {
    let newFilters = { ...filters };
    if (name === 'sector') {
      // reset industry if sector change
      newFilters = { ...newFilters, industry: null };
    }
    newFilters = { ...newFilters, [name]: option?.value ? option?.label : null };

    changeFilters(newFilters);
  };

  return (
    <div className='pack-assignment-filters-container mt-3'>
      <div className='d-flex justify-content-between align-items-center gap-3' style={{ height: '36px' }}>
        <Input placeholder={'Search for a company...'} onChange={handleChangeSearchText} value={searchText} />
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          placeholder='Sector'
          isClearable
          options={sectorOptions}
          value={sectorOptions.find((item) => item.label === filters.sector) ?? null}
          onChange={(option) => handleChangeSectorIndustryFilters('sector', option)}
        />
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          placeholder='Industry'
          isClearable
          options={industryOptions}
          value={industryOptions.find((item) => item.label === filters.industry) ?? null}
          onChange={(option) => handleChangeSectorIndustryFilters('industry', option)}
          isDisabled={!filters.sector}
        />
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          placeholder='Data share status'
          isClearable
          options={dataShareStatusOptions}
          value={
            dataShareStatusOptions.find((item) => item.value === filters.dataShareStatus && item.value !== null) ?? null
          }
          onChange={(option) => handleChangeFilters('dataShareStatus', option)}
        />
      </div>
    </div>
  );
};
